/**
 * DialPadModal.jsx — Global Dial Pad
 * v3.15-579: Manual dial pad for calling any phone number (not tied to a client record).
 * Accessible from the top nav. Uses existing Twilio telephony system.
 * v3.15-1131: Adds Transfer Active Call button (visible only when there is
 * an active outbound call for this user).
 */
import { useEffect, useState } from 'react';
import { Phone, PhoneOff, PhoneForwarded, Delete, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import GhostModalHeader from './GhostModalHeader';
import TransferCallModal from './TransferCallModal';
// v3.21-1634 · P0 client-local-time late-call safeguard
import useOutboundCall from '../hooks/useOutboundCall';
import LateCallWarningModal from './LateCallWarningModal';
// v3.21-1753 · Optional Browser Headset mode — one-click opt-in.
import BrowserHeadsetToggle from './BrowserHeadsetToggle';

const formatDisplay = (digits) => {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const KEYS = [
  { label: '1', sub: '' },
  { label: '2', sub: 'ABC' },
  { label: '3', sub: 'DEF' },
  { label: '4', sub: 'GHI' },
  { label: '5', sub: 'JKL' },
  { label: '6', sub: 'MNO' },
  { label: '7', sub: 'PQRS' },
  { label: '8', sub: 'TUV' },
  { label: '9', sub: 'WXYZ' },
  { label: '*', sub: '' },
  { label: '0', sub: '+' },
  { label: '#', sub: '' },
];

const DialPadModal = ({ isOpen, onClose, initialNumber = '' }) => {
  const { user } = useAuth();
  const [digits, setDigits] = useState('');
  const [callAs, setCallAs] = useState('eqf_hub');
  // v3.21-1634 · Shared outbound-call hook with client-local-time preflight.
  const {
    calling, initiateOutboundCall,
    preflight, proceedLateCall, cancelLateCall, lateCallWarningOpen,
  } = useOutboundCall();
  // v3.21-1612 · The active-call controller (ActiveCallPill) is now the
  // SSOT for disposition + notes + End Call.  No post-call outcome
  // modal is opened from the dial pad any more.
  // v3.15-1131: track the most recent active call so the Transfer button
  // inside the dial pad knows what to transfer.
  const [activeCall, setActiveCall] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);

  // v3.18-1296 — preseed digits when opened with an initialNumber
  // (Contact Directory click-to-call). Only normalises when opened.
  useEffect(() => {
    if (isOpen && initialNumber) {
      const cleaned = String(initialNumber).replace(/\D/g, '').slice(-10);
      setDigits(cleaned);
    }

  }, [isOpen, initialNumber]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get('/telephony/active-call');
        if (!cancelled) setActiveCall(r.data?.active || null);
      } catch { /* noop */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isOpen]);

  const handleKey = (key) => {
    if (digits.length >= 10 && key !== '*' && key !== '#') return;
    setDigits((prev) => prev + key);
  };

  const handleBackspace = () => {
    setDigits((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setDigits('');
  };

  const handleCall = async () => {
    const cleaned = digits.replace(/\D/g, '');
    if (cleaned.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    if (!user) {
      toast.error('You must be signed in to place a call');
      return;
    }
    const e164 = cleaned.length === 10 ? `+1${cleaned}` : `+${cleaned}`;
    const result = await initiateOutboundCall(e164, {
      callAs,
      context: 'dial_pad_modal',
    });
    // If the preflight fired the warning modal, we do NOT close the
    // dial pad — we let the warning drive Cancel / Proceed Anyway.
    // If the call was actually placed (or preflight ok), reset + close.
    if (result && result.ok) {
      setDigits('');
      onClose();
    }
  };

  const handleClose = () => {
    setDigits('');
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <DialogContent className="sm:max-w-[360px] p-0 overflow-hidden rounded-2xl shadow-2xl border-0" data-testid="dial-pad-modal">
          {/* v3.18-1303 — Radix a11y contract (prevents strict-mode throw). */}
          <DialogTitle className="sr-only">Dial Pad</DialogTitle>
          <DialogDescription className="sr-only">Place a manual outbound call.</DialogDescription>
          <GhostModalHeader subtitle="Dial Pad" />
          <div className="flex flex-col items-center px-5 py-4 space-y-4">
            {/* Phone number display */}
            <div className="w-full text-center min-h-[48px] flex items-center justify-center" data-testid="dial-pad-display">
              <span className={`font-mono tracking-wider ${digits ? 'text-2xl font-bold text-slate-900' : 'text-lg text-slate-400'}`}>
                {digits ? formatDisplay(digits) : 'Enter number'}
              </span>
            </div>

            {/* Keypad grid */}
            <div className="grid grid-cols-3 gap-2 w-full max-w-[280px]" data-testid="dial-pad-keypad">
              {KEYS.map(({ label, sub }) => (
                <button
                  key={label}
                  onClick={() => handleKey(label)}
                  className="flex flex-col items-center justify-center h-14 rounded-xl bg-slate-50 hover:bg-slate-100 active:bg-slate-200 transition-all duration-100 select-none border border-slate-100"
                  data-testid={`dial-key-${label === '*' ? 'star' : label === '#' ? 'hash' : label}`}
                >
                  <span className="text-xl font-semibold text-slate-800 leading-none">{label}</span>
                  {sub && <span className="text-[9px] text-slate-400 tracking-widest mt-0.5">{sub}</span>}
                </button>
              ))}
            </div>

            {/* Call As selector */}
            <div className="w-full max-w-[280px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Call As</label>
              <Select value={callAs} onValueChange={setCallAs}>
                <SelectTrigger className="w-full h-9 text-sm" data-testid="dial-pad-call-as">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eqf_hub">EasyQuickFund / PTC Hub</SelectItem>
                  <SelectItem value="ptc_travel">Prestigious Travel Club</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 w-full max-w-[280px]">
              <button
                onClick={handleBackspace}
                onDoubleClick={handleClear}
                disabled={!digits}
                className="p-3 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-30 transition-colors"
                title="Backspace (double-click to clear)"
                data-testid="dial-pad-backspace"
              >
                <Delete className="h-5 w-5 text-slate-600" />
              </button>

              <Button
                onClick={handleCall}
                disabled={calling || digits.replace(/\D/g, '').length < 10}
                className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base"
                data-testid="dial-pad-call-btn"
              >
                {calling ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Calling...</>
                ) : (
                  <><Phone className="h-5 w-5 mr-2" /> Call</>
                )}
              </Button>

              <button
                onClick={handleClose}
                className="p-3 rounded-xl bg-red-50 hover:bg-red-100 transition-colors"
                data-testid="dial-pad-close"
              >
                <PhoneOff className="h-5 w-5 text-red-500" />
              </button>
            </div>

            {/* v3.21-1753 · Optional Browser Headset mode.  DEFAULT is
                Standard Calling; this button only activates the browser
                Twilio Device when explicitly clicked. */}
            <div className="w-full max-w-[280px] flex justify-center">
              <BrowserHeadsetToggle />
            </div>

            {/* v3.15-1131: Transfer button — visible only when there is a
                live call so the dial pad doubles as an active-call controller. */}
            {activeCall?.call_log_id && (
              <button
                type="button"
                onClick={() => setTransferOpen(true)}
                className="w-full max-w-[280px] flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                data-testid="dial-pad-transfer-btn"
              >
                <PhoneForwarded className="h-4 w-4" />
                Transfer Active Call
                <span className="text-[11px] font-normal opacity-80 ml-1">
                  · {activeCall.client_name || activeCall.client_number || 'in progress'}
                </span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* v3.21-1612 · Post-call outcome modal removed — ActiveCallPill
          is now the SSOT for disposition + notes + End Call. */}

      {/* v3.15-1131: in-dialpad Transfer modal */}
      <TransferCallModal
        isOpen={transferOpen}
        onClose={() => setTransferOpen(false)}
        activeCall={activeCall}
        onTransferred={() => { setTransferOpen(false); }}
      />

      {/* v3.21-1634 · P0 client-local-time late-call safeguard */}
      <LateCallWarningModal
        open={lateCallWarningOpen}
        preflight={preflight}
        onCancel={cancelLateCall}
        onProceed={async () => {
          const r = await proceedLateCall();
          if (r && r.ok) { setDigits(''); onClose(); }
        }}
        proceeding={calling}
      />
    </>
  );
};

export default DialPadModal;
